'use client';

import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { UserData } from '../lib/custom-types';
import { cleanLocalStorage, safeGetItem, safeSetItem } from '../lib/localStorage-cleaner';
import { useRouter, usePathname } from 'next/navigation';
import useTokenManager from '../hooks/useTokenManager';

// Type pour le contexte d'authentification
type AuthContextType = {
  user: UserData | undefined; // Peut être undefined si non chargé ou profil vide
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  authError: string | null;
};

// Création du contexte
const AuthContext = createContext<AuthContextType>({
  user: undefined,
  isAuthenticated: false,
  login: async () => {},
  logout: () => {},
  isLoading: false,
  authError: null
});

// Convertir UserProfile en UserData
function convertUserProfileToUserData(profile: any): UserData | undefined {
  console.log('[DEBUG convertUserProfileToUserData] profile reçu:', profile);
  if (!profile) {
    console.warn('🔐 [AuthContext] convertUserProfileToUserData: profile est null ou undefined');
    return undefined;
  }
  
  // Au minimum, nous avons besoin d'un identifiant unique. Si aucun n'est fourni, on en crée un.
  const userId = profile.id ?? profile.sub ?? profile.user_id ?? `temp-${Date.now()}`;
  const username = profile.username ?? profile.email ?? profile.preferred_username ?? profile.name ?? 'Utilisateur';
  
  console.log('🔐 [AuthContext] Identifiants extraits:', { userId, username });
  
  // Conversion robuste avec fallback pour chaque champ
  const userData: UserData = {
    id: userId,
    username: username,
    email: profile.email ?? `${username}@example.com`,
    first_name: profile.first_name ?? '',
    last_name: profile.last_name ?? '',
    is_active: profile.is_active ?? true, 
    is_staff: profile.is_staff ?? false,
    is_superuser: profile.is_superuser ?? false,
    tenant_id: profile.tenant_id || profile.current_tenant?.id || null,
    tenant_name: profile.tenant_name || profile.current_tenant?.name || null,
    tenant_role: profile.tenant_role || profile.current_tenant?.role || 'member',
    tenants: profile.tenants || [],
    last_login: profile.last_login
      ? (profile.last_login instanceof Date
        ? profile.last_login.toISOString()
        : profile.last_login)
      : undefined,
    date_joined: profile.date_joined
      ? (profile.date_joined instanceof Date
        ? profile.date_joined.toISOString()
        : profile.date_joined)
      : undefined
  };
  
  console.log('🔐 [AuthContext] UserData généré:', userData);
  return userData;
}

// Fournisseur du contexte
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const pathname = usePathname() || '';
  const [user, setUser] = useState<UserData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const tokenManager = useTokenManager();

  // Nettoyer le localStorage au chargement
  useEffect(() => {
    cleanLocalStorage();
  }, []);

  // DEV OVERRIDE: always authenticated in development (auto-login bypass complet)
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_DEV_BYPASS === 'true') {
      setUser({
        id: 0,
        username: 'dev_user',
        email: 'demo@example.com',
        first_name: 'Dev',
        last_name: 'User',
        is_active: true,
        is_staff: true,
        is_superuser: true,
        tenant_id: null,
        tenant_name: null,
        tenant_role: 'admin',
        tenants: [],
        last_login: new Date().toISOString(),
        date_joined: new Date().toISOString(),
      });
      setIsLoading(false);
      setAuthError(null);
      // Redirige automatiquement vers le dashboard si on est sur /login ou /register
      if (window.location.pathname === '/login' || window.location.pathname === '/register') {
        window.location.href = '/dashboard';
      }
      return;
    }

  // Vérifier l'authentification au chargement
  // Vérification robuste de l'authentification et chargement du profil utilisateur
    const checkAuth = async () => {
      setIsLoading(true);
      setAuthError(null); // UX silencieuse : pas d'affichage d'erreur à l'utilisateur
      try {
        console.log('🔐 [AuthContext] Début de la vérification d\'authentification...');
        
        // Vérification du mode démo/fallback
        const isPathLogin = pathname === '/login' || pathname === '/register';
        const backendAvailable = await authAPI.isBackendAvailable().catch(() => false);
        
        if (!backendAvailable && !isPathLogin) {
          console.log('🔐 [AuthContext] Backend indisponible, vérification du mode démo...');
          const demoUserData = safeGetItem<UserData | null>('user_data', null);
          
          // Si un utilisateur démo est déjà connecté
          if (demoUserData && demoUserData.email === 'demo@example.com') {
            console.log('🔐 [AuthContext] Utilisateur démo détecté, continuant en mode démo');
            setUser(demoUserData);
            setIsLoading(false);
            return;
          }
        }
        
        // Vérification directe des données utilisateur stockées avant tout
        const storedUser = safeGetItem<UserData | null>('user_data', null);
        const userProfile = await tokenManager.getUserProfile();
        
        console.log('🔐 [AuthContext] État initial:', {
          storedUserData: storedUser ? 'présent' : 'absent',
          userProfile: userProfile ? 'présent' : 'absent',
        });
        
        // Si on a un utilisateur stocké et un token, court-circuiter pour éviter des appels API inutiles
        if (storedUser && userProfile) {
          console.log('🔐 [AuthContext] Utilisateur déjà en cache avec token valide');
          setUser(storedUser);
          setIsLoading(false);
          // Vérification de l'authenticité de l'utilisateur si déjà connecté
          const isAuthenticated = tokenManager.isAuthenticated();
          console.log('🔐 [AuthContext] Résultat de isAuthenticated:', isAuthenticated);
        } else {
          // Cas 1: utilisateur déjà authentifié et storedUser existe
          const isAuthenticated = tokenManager.isAuthenticated();
          console.log('🔐 [AuthContext] Résultat de isAuthenticated:', isAuthenticated);

          if (isAuthenticated) {
            let userData: UserData | null = null;
            let apiError: any = null;

            // 1. Tentative principale : API
            try {
              const apiUser = await authAPI.getCurrentUser();
              console.log('🔐 [AuthContext] Utilisateur récupéré via API:', apiUser);
              if (apiUser) {
                userData = convertUserProfileToUserData(apiUser) || null;
              }
            } catch (err) {
              apiError = err;
              console.warn('🔐 [AuthContext] Erreur lors de la récupération du profil via API:', err);
            }

            // 2. Si échec, tenter un refresh token + retry
            if (!userData) {
              try {
                await authAPI.refreshToken();
                const refreshedUser = await authAPI.getCurrentUser();
                console.log('🔐 [AuthContext] Utilisateur après refresh token:', refreshedUser);
                if (refreshedUser) {
                  userData = convertUserProfileToUserData(refreshedUser) || null;
                }
              } catch (refreshErr) {
                console.warn('🔐 [AuthContext] Refresh token + retry échoué:', refreshErr);
              }
            }

            // 3. Fallback ultime : extraction du token JWT
            if (!userData) {
              const accessToken = tokenManager.getAccessToken();
              if (accessToken) {
                try {
                  const tokenInfo = await tokenManager.decodeToken(accessToken);
                  if (tokenInfo && (tokenInfo.username || tokenInfo.preferred_username)) {
                    userData = {
                      id: tokenInfo.sub || tokenInfo.user_id || null,
                      username: tokenInfo.username || tokenInfo.preferred_username || '',
                      email: tokenInfo.email || '',
                      first_name: tokenInfo.first_name || '',
                      last_name: tokenInfo.last_name || '',
                      is_active: true,
                      is_staff: tokenInfo.is_staff || false,
                      is_superuser: tokenInfo.is_superuser || false,
                      tenant_id: tokenInfo.tenant_id || null,
                      tenant_name: tokenInfo.tenant_name || null,
                      tenant_role: tokenInfo.tenant_role || 'member',
                      tenants: tokenInfo.tenants || [],
                      last_login: tokenInfo.last_login || undefined,
                      date_joined: tokenInfo.date_joined || undefined
                    };
                    console.log('🔐 [AuthContext] Utilisateur extrait du token JWT:', userData);
                  }
                } catch (tokenErr) {
                  console.error('🔐 [AuthContext] Extraction du profil depuis le token JWT échouée:', tokenErr);
                }
              }
            }

            // 4. Fallback localStorage : si on n'a pas trouvé de userData par les autres moyens
            if (!userData) {
              try {
                const storedUser = safeGetItem<UserData | null>('user_data', null);
                console.log('🔐 [AuthContext] Tentative de récupération depuis localStorage:', storedUser);
                
                if (storedUser && storedUser.username) {
                  console.log('🔐 [AuthContext] Utilisateur trouvé dans localStorage');
                  userData = storedUser;
                }
              } catch (localStorageErr) {
                console.error('🔐 [AuthContext] Erreur lors de la récupération depuis localStorage:', localStorageErr);
              }
            }

            // 5. Gestion finale de l'état utilisateur
            if (userData) {
              console.log('🔐 [AuthContext] Avant convertUserProfileToUserData, userData:', userData);
              const convertedUser = convertUserProfileToUserData(userData);
              console.log('🔐 [AuthContext] Après convertUserProfileToUserData, convertedUser:', convertedUser);
              
              if (convertedUser) {
                safeSetItem('user_data', convertedUser);
                console.log('🔐 [AuthContext] Mise à jour du state avec setUser(convertedUser)');
                setUser(convertedUser);
                setAuthError(null);
              } else {
                console.warn('🔐 [AuthContext] convertUserProfileToUserData a retourné undefined pour:', userData);
                setUser(undefined);
                setAuthError(null);
              }
            } else {
              console.warn('🔐 [AuthContext] Aucun profil utilisateur trouvé par aucune méthode');
              setUser(undefined);
              setAuthError(null); // UX silencieuse : pas d'affichage d'erreur à l'utilisateur
            }
          } else {
            // UX silencieuse : pas d'affichage d'erreur d'authentification
            setUser(undefined);
            setAuthError(null); // On ne montre rien à l'utilisateur
          }
        }
      } catch (outerErr) {
        console.error('🔐 [AuthContext] Erreur critique dans checkAuth:', outerErr);
        setUser(undefined);
        setAuthError(null); // UX silencieuse : pas d'affichage d'erreur à l'utilisateur
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fonction de connexion
  const login = async (username: string, password: string) => {
  // BYPASS DEV ABSOLU : si on est en dev et que c'est le compte demo, connecte direct
  if (process.env.NODE_ENV === 'development' && username === 'demo@example.com' && password === 'password') {
    setUser({
      id: 0,
      username: 'dev_user',
      email: 'demo@example.com',
      first_name: 'Dev',
      last_name: 'User',
      is_active: true,
      is_staff: true,
      is_superuser: true,
      tenant_id: null,
      tenant_name: null,
      tenant_role: 'admin',
      tenants: [],
      last_login: new Date().toISOString(),
      date_joined: new Date().toISOString(),
    });
    setIsLoading(false);
    setAuthError(null);
    return;
  }

    console.log('🔐 Tentative de connexion pour:', username);
    setIsLoading(true);
    setAuthError(null);
    
    // Mode fallback - Vérification pour le mode démo/dev quand le backend est indisponible
    const isDemoAccount = username === 'demo@example.com' && password === 'password';
    const isDevMode = process.env.NODE_ENV !== 'production' || !authAPI.isBackendAvailable();
    
    if (isDemoAccount && isDevMode) {
      console.log('🔐 [AuthContext] Mode démo/dev détecté, utilisation du compte de démo');
      try {
        // Création d'un utilisateur fictif pour le mode démo
        const demoUser: any = {
          id: 999, // Utiliser un nombre pour satisfaire l'interface UserData
          username: 'demo_user',
          email: 'demo@example.com',
          first_name: 'Utilisateur',
          last_name: 'Démo',
          is_active: true,
          is_staff: true,
          is_superuser: false,
          tenant_id: 'demo-tenant-123',
          tenant_name: 'Organisation Démo',
          tenant_role: 'admin',
          tenants: [
            { id: 'demo-tenant-123', name: 'Organisation Démo', role: 'admin' },
            { id: 'demo-tenant-456', name: 'Organisation Test', role: 'member' }
          ],
          last_login: new Date().toISOString(),
          date_joined: '2023-01-01T00:00:00Z'
        };
        
        // Simuler un délai d'authentification pour l'UX
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Stocker les données utilisateur pour la session
        safeSetItem('user_data', demoUser);
        setUser(demoUser);
        
        // Si ce n'est pas le mode démo, créer des faux tokens pour simuler une connexion
        console.log('🔐 [AuthContext] Mode démo activé - Création de faux tokens');
        
        // Créer un faux token et un faux profil utilisateur
        const fakeTokens = {
          access_token: 'fake-demo-token',
          refresh_token: 'fake-demo-refresh-token',
          expires_in: 3600
        };
        
        // Stocker les tokens de manière sécurisée
        await tokenManager.setTokens(fakeTokens);
        
        setIsLoading(false);
        
        // Rediriger vers le dashboard si sur la page de login ou d'inscription
        if (pathname === '/login' || pathname === '/register') {
          router.push('/dashboard');
        }
        
        return;
      } catch (error) {
        console.error('🔐 [AuthContext] Erreur lors de la création du compte démo:', error);
      }
    }
    
    // Mode normal - Tentative de connexion au backend
    let lastError: any = null;
    
    try {
      // Formats d'authentification à essayer
      const authMethods = [
        // Format 1: Standard avec username et password
        () => authAPI.login({
          username, 
          password
        } as any),
        
        // Format 2: Avec email explicite
        () => authAPI.login({
          username, 
          password,
          email: username.includes('@') ? username : `${username}@example.com`
        } as any),
        
        // Format 3: Avec username comme champ principal et email
        () => authAPI.login({ 
          username, 
          password, 
          email: username 
        } as any),
        
        // Format 4: Format avec les champs dans un objet user
        () => authAPI.login({ 
          user: {
            username, 
            password,
            email: username.includes('@') ? username : `${username}@example.com`
          }
        } as any)
      ];
      
      // Essayer chaque méthode d'authentification jusqu'à ce qu'une fonctionne
      let lastError;
      
      for (const method of authMethods) {
        try {
          const response = await method();
          console.log('🔐 Login successful with method:', method.toString());
          
          // Extraire le token de la réponse (supporte différents formats de réponse)
          // La réponse peut être directement un UserProfile ou un objet avec des tokens
          console.log('🔐 Réponse de connexion reçue:', response);
          
          // Gérer différents formats de réponse
          let accessToken = '';
          let refreshToken = '';
          let expiresIn = 3600; // 1 heure par défaut
          
          // Format 1: { access_token, refresh_token }
          if ((response as any)?.access_token) {
            accessToken = (response as any).access_token;
            refreshToken = (response as any).refresh_token || '';
          }
          // Format 2: { access, refresh }
          else if ((response as any)?.access) {
            accessToken = (response as any).access;
            refreshToken = (response as any).refresh || '';
          }
          // Format 3: { token: { access, refresh } }
          else if ((response as any)?.token?.access) {
            accessToken = (response as any).token.access;
            refreshToken = (response as any).token.refresh || '';
          }
          // Format 4: Réponse directe avec token dans le corps
          else if (typeof response === 'string') {
            accessToken = response;
          }
          
          if (!accessToken) {
            console.error('🔐 Aucun token d\'accès trouvé dans la réponse:', response);
            throw new Error('Aucun token d\'accès reçu du serveur');
          }
          
          console.log('🔐 Tokens extraits:', { 
            accessToken: accessToken ? 'présent' : 'manquant',
            refreshToken: refreshToken ? 'présent' : 'manquant'
          });
          
          // Stocker les tokens de manière sécurisée
          await tokenManager.setTokens({
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: Date.now() + (expiresIn * 1000) // Convertir en millisecondes
          });
          console.log('🔐 Tokens stockés avec succès');
          
          // Essayer de récupérer les données utilisateur
          const userData = await authAPI.getCurrentUser();
          console.log('🔐 Données utilisateur reçues:', userData);
          
          // Convertir UserProfile en UserData avec support tenant
          const convertedUser = convertUserProfileToUserData(userData);
          console.log('[DEBUG AuthProvider] convertedUser:', convertedUser);
          // Stocker les données utilisateur de manière sécurisée
          safeSetItem('user_data', convertedUser);
          setUser(convertedUser);
          // Redirection vers le dashboard si sur la page de login ou d'inscription
          if (pathname === '/login' || pathname === '/register') {
            router.push('/dashboard');
          }
          
          return;
        } catch (error) {
          console.warn('🔐 Login attempt failed:', error);
          lastError = error;
          continue;
        }
      }
      
      // Si nous arrivons ici, toutes les méthodes ont échoué
      throw lastError || new Error('Toutes les méthodes d\'authentification ont échoué');
    } catch (error: any) {
      console.error('🔐 Erreur de connexion détaillée:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      let errorMessage = 'Échec de la connexion. Veuillez vérifier vos identifiants.';
      
      if (error.response) {
        // Erreur avec réponse du serveur
        const { status, data } = error.response;
        
        if (status === 400 || status === 401) {
          errorMessage = data?.detail || data?.message || 'Identifiants incorrects';
        } else if (status >= 500) {
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
        }
      } else if (error.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        errorMessage = 'Pas de réponse du serveur. Vérifiez votre connexion internet.';
      }
      
      setAuthError(errorMessage);
      throw new Error(errorMessage);
    }
  };
  
  // Fonction de déconnexion
  const logout = async () => {
    console.log('🔐 Déconnexion de l\'utilisateur');
    console.log('[DEBUG logout] Avant suppression tokens/user_data:', {
      user,
      localUser: localStorage.getItem('user_data')
    });
    
    try {
      // Supprimer les tokens et les données utilisateur
      await tokenManager.clearTokens();
      localStorage.removeItem('user_data');
      // Réinitialiser l'état
      setUser(undefined);
      setAuthError(null);
      // Rediriger vers la page de connexion
      router.push('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      // Fallback en cas d'erreur
      localStorage.removeItem('user_data');
      localStorage.removeItem('sentinel_access_token');
      localStorage.removeItem('sentinel_refresh_token');
      localStorage.removeItem('sentinel_user_profile');
      setUser(undefined);
      router.push('/login');
    }
  };
  
  // Valeur du contexte
  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading,
    authError
  };
  
  return (
    <AuthContext.Provider value={contextValue}>

      {/* Loading Spinner Overlay */}
      {isLoading && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#111',padding:32,borderRadius:12,color:'#0ff',fontSize:20,fontFamily:'monospace',boxShadow:'0 0 32px #0ff8'}}>
            Chargement du profil utilisateur...
          </div>
        </div>
      )}
      {/* Error Overlay */}
      {authError && !isLoading && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.7)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#222',padding:32,borderRadius:12,color:'#f66',fontSize:20,fontFamily:'monospace',boxShadow:'0 0 32px #f668'}}>
            <div style={{marginBottom:16}}>Erreur : {authError}</div>
            <button style={{background:'#0ff',color:'#111',padding:'8px 24px',border:'none',borderRadius:6,fontWeight:600,cursor:'pointer'}} onClick={()=>window.location.reload()}>Réessayer</button>
          </div>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  console.log('[DEBUG useAuth export] context:', context);
  return context;
};

export default AuthContext;
